import React, { useEffect, useState } from 'react';
import type { EngineStatus } from '@pagepilot/domain';
import { Badge } from './ui';
import { PagesScreen } from './screens/Pages';
import { PageWorkspace } from './screens/PageWorkspace';
import { AnalyticsScreen } from './screens/Analytics';
import { AssetLibraryScreen } from './screens/AssetLibrary';
import { BackupScreen } from './screens/Backup';
import { AboutScreen } from './screens/About';
import { PromptLibraryScreen } from './screens/PromptLibrary';
import { ProvidersScreen } from './screens/Providers';
import { ActivityScreen } from './screens/Activity';
import { SettingsScreen } from './screens/Settings';
import { QueueScreen } from './screens/Queue';

// Global (app-level) tools reachable from the home screen. Everything else is
// page-scoped and lives inside a Page workspace (project model).
type AppScreen = 'providers' | 'prompts' | 'assets' | 'analytics' | 'queue' | 'activity' | 'backup' | 'settings' | 'about';

type View =
  | { kind: 'home' }
  | { kind: 'page'; pageId: string; label: string }
  | { kind: 'app'; screen: AppScreen };

const APP_NAV: { id: AppScreen; label: string }[] = [
  { id: 'providers', label: 'AI Providers' },
  { id: 'prompts', label: 'Prompt Library' },
  { id: 'assets', label: 'Asset Library (all)' },
  { id: 'analytics', label: 'Performance (all)' },
  { id: 'queue', label: 'Publishing Queue (all)' },
  { id: 'activity', label: 'Activity & Logs' },
  { id: 'backup', label: 'Backup & Restore' },
  { id: 'settings', label: 'Settings' },
  { id: 'about', label: 'About & Diagnostics' },
];

const LAST_PAGE_KEY = 'pagepilot.lastPageId';

export function App() {
  const [view, setView] = useState<View>({ kind: 'home' });
  const [status, setStatus] = useState<EngineStatus | null>(null);
  const [engineError, setEngineError] = useState<string | null>(null);
  const [navOpen, setNavOpen] = useState(false); // mobile sidebar drawer

  const refreshStatus = async () => {
    try {
      const s = (await window.pagepilot.engine.status()) as EngineStatus;
      setStatus(s);
      setEngineError(null);
    } catch (e) {
      setStatus(null);
      setEngineError(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    void refreshStatus();
    const t = setInterval(() => void refreshStatus(), 10_000);
    return () => clearInterval(t);
  }, []);

  const openPage = (pageId: string, label: string) => {
    localStorage.setItem(LAST_PAGE_KEY, pageId);
    setView({ kind: 'page', pageId, label });
  };

  return (
    <div className="flex h-screen flex-col">
      {/* Engine + provider health bar (spec §5.1) */}
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-neutral-800 bg-neutral-950/80 px-4 py-2.5 sm:px-5">
        {view.kind !== 'page' && (
          <button
            onClick={() => setNavOpen((o) => !o)}
            className="rounded-lg p-1.5 text-neutral-300 hover:bg-neutral-800 md:hidden"
            aria-label="Toggle menu"
          >
            <span className="block h-4 w-5 text-base leading-none">☰</span>
          </button>
        )}
        <button onClick={() => { setView({ kind: 'home' }); setNavOpen(false); }} className="flex items-center gap-2 pr-2 text-left">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-500 text-xs font-bold text-white">▶</div>
          <span className="text-sm font-semibold">PagePilot AI</span>
        </button>
        <span className="hidden text-xs font-medium text-neutral-400 sm:inline">Engine:</span>
        {status ? (
          <Badge status={status.state === 'running' ? 'HEALTHY' : status.state === 'degraded' ? 'RATE_LIMITED' : 'OFFLINE'} label={status.state} />
        ) : (
          <Badge status="OFFLINE" label={engineError ? 'unreachable' : 'connecting…'} />
        )}
        <div className="hidden flex-wrap items-center gap-1.5 sm:flex">
          {status?.providers.map((p) => (
            <span key={p.kind} title={p.lastError ?? undefined}>
              <Badge status={p.status} label={`${p.kind.replace('-cli', '').replace('-video', '').replace('-image', '')}: ${p.status.toLowerCase().replaceAll('_', ' ')}`} />
            </span>
          ))}
        </div>
        <div className="ml-auto hidden text-xs text-neutral-500 md:block">
          {status && `Queue: ${status.queue.pending} pending · ${status.queue.running} running · ${status.queue.blocked} blocked`}
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        {view.kind === 'page' ? (
          <PageWorkspace
            pageId={view.pageId}
            pageLabel={view.label}
            status={status}
            onBack={() => setView({ kind: 'home' })}
          />
        ) : (
          <>
            {/* Global sidebar: Home (page list) + app-level tools.
                On mobile it's a collapsible panel toggled from the header. */}
            <aside className={`${navOpen ? 'flex' : 'hidden'} w-full shrink-0 flex-col border-b border-neutral-800 bg-neutral-950 md:flex md:w-56 md:border-b-0 md:border-r`}>
              <nav className="mt-3 flex-1 space-y-0.5 overflow-y-auto px-2 pb-3">
                <button
                  onClick={() => { setView({ kind: 'home' }); setNavOpen(false); }}
                  className={`block w-full rounded-lg px-3 py-2 text-left text-sm ${view.kind === 'home' ? 'bg-brand-500/15 text-brand-300' : 'text-neutral-400 hover:bg-neutral-900 hover:text-neutral-200'}`}
                >
                  Pages
                </button>
                <p className="px-3 pb-1 pt-4 text-[10px] uppercase tracking-wide text-neutral-600">App tools</p>
                {APP_NAV.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => { setView({ kind: 'app', screen: n.id }); setNavOpen(false); }}
                    className={`block w-full rounded-lg px-3 py-2 text-left text-sm ${view.kind === 'app' && view.screen === n.id ? 'bg-brand-500/15 text-brand-300' : 'text-neutral-400 hover:bg-neutral-900 hover:text-neutral-200'}`}
                  >
                    {n.label}
                  </button>
                ))}
              </nav>
              <div className="hidden border-t border-neutral-800 p-3 text-[11px] text-neutral-500 md:block">
                Runs in the cloud — generation and publishing continue even when this tab is closed.
              </div>
            </aside>

            <main className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
              {view.kind === 'home' && (
                <PagesScreen onOpen={openPage} />
              )}
              {view.kind === 'app' && view.screen === 'providers' && <ProvidersScreen status={status} />}
              {view.kind === 'app' && view.screen === 'prompts' && <PromptLibraryScreen />}
              {view.kind === 'app' && view.screen === 'assets' && <AssetLibraryScreen />}
              {view.kind === 'app' && view.screen === 'analytics' && <AnalyticsScreen />}
              {view.kind === 'app' && view.screen === 'queue' && <QueueScreen />}
              {view.kind === 'app' && view.screen === 'activity' && <ActivityScreen />}
              {view.kind === 'app' && view.screen === 'backup' && <BackupScreen />}
              {view.kind === 'app' && view.screen === 'settings' && <SettingsScreen />}
              {view.kind === 'app' && view.screen === 'about' && <AboutScreen />}
            </main>
          </>
        )}
      </div>
    </div>
  );
}
